import { useState, useEffect, useMemo, useRef } from 'react';
import { formatDateToMDY } from '../../../constants/utils';
import {
  EXPIRY_DATE,
  LAST_INSTALLATION_DATE,
  LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
  SWEETNER_DISCLAIMER,
} from '../constants';
import { getExpiryDate } from '../service/CreatePromotionService';

export const useOfferSweetners = ({
  offersList = [],
  _defaultExpiryDate,
  promotionData,
  setPromotionData,
  mode,
  loadedPromotionData,
}) => {
  const [showDatePickerForOfferSweetnerID, setShowDatePickerForOfferSweetnerID] = useState(1);
  const [includeSweetnerOffer, setIncludeSweetnerOffer] = useState(false);
  const [configureHeadline, setConfigureHeadline] = useState(false);
  const [headlineOfferValues, setHeadlineOfferValues] = useState({});
  const [selectHeadline, setSelectHeadline] = useState(null);
  const [disableConfig, setDisableConfig] = useState(true);
  const [variableValues, setVariableValues] = useState({});
  const [expiryDatesObj, setExpiryDatesObj] = useState([]);
  const [customHeadline, setCustomHeadline] = useState('');
  const [promoExpiry, setPromoExpiry] = useState({});
  const [valuesForD1, setValuesForD1] = useState([]);
  const [valuesForD2, setValuesForD2] = useState([]);
  const [valuesForD3, setValuesForD3] = useState([]);
  const [customDisclaimer, setCustomDisclaimer] = useState('');
  const [isSweetnerClickable, setIsSweetnerClickable] = useState(false);
  const valuesLoadedInitially = useRef(mode !== 'Edit');
  const [includeAppointmentDisclaimer, setIncludeAppointmentDisclaimer] = useState(false);

  const modifiedOfferData = useMemo(() => {
    const customOffer = {
      label: 'Custom Offer (Click Configure button to set value)',
      value: 'custom_offer',
    };
    const noOffer = {
      label: 'None',
      value: 'none',
      id: 'none-headline',
    };
    const allOffers = offersList.map((eachOffer) => ({
      label: eachOffer.template,
      value: eachOffer.id,
      ...eachOffer,
    }));

    return [noOffer, ...allOffers, customOffer];
  }, [offersList]);

  useEffect(() => {
    // Initialize valuesForD1 based on offersList if needed
    const initializeD1Values = () => {
      const selectedOffer = offersList.find((offer) => offer.id === selectHeadline);

      if (selectedOffer?.nested_values) {
        setValuesForD1(Object.keys(selectedOffer.nested_values));
      }
    };

    if (selectHeadline && selectHeadline !== 'none' && selectHeadline !== 'custom_offer') {
      initializeD1Values();
    }

    const { discount_offer, finance_offer, sweetener_offer } = promotionData;

    // Extract nested conditions into more readable statements
    const hasDiscountOffer =
      discount_offer !== undefined &&
      discount_offer !== null &&
      Object.keys(discount_offer).length !== 0;

    const hasFinanceOffer =
      finance_offer !== undefined &&
      finance_offer !== null &&
      Object.keys(finance_offer).length !== 0;

    const shouldLoadInitialValues =
      loadedPromotionData &&
      Object.keys(promotionData).length > 0 &&
      !valuesLoadedInitially.current;

    if (hasDiscountOffer) {
      setIsSweetnerClickable(true);
    }

    if (hasFinanceOffer) {
      setIsSweetnerClickable(true);
    }

    if (shouldLoadInitialValues) {
      if (sweetener_offer && Object.keys(sweetener_offer).length !== 0) {
        setIncludeSweetnerOffer(true);

        if (sweetener_offer?.initial_appointment_disclaimer) {
          setIncludeAppointmentDisclaimer(true);
        }

        // Extract nested ternary into simpler conditional
        let headlineValue;

        if (sweetener_offer.custom) {
          headlineValue = 'custom_offer';
        } else if (sweetener_offer.headline_builder_id) {
          headlineValue = sweetener_offer.headline_builder_id;
        } else {
          headlineValue = 'none';
        }
        setSelectHeadline(headlineValue);

        setCustomHeadline(sweetener_offer.headline || '');
        setCustomDisclaimer(sweetener_offer.disclaimer || '');

        const expiryDates =
          sweetener_offer.offer_expiry !== undefined
            ? getExpiryDate(sweetener_offer.offer_expiry)
            : [];

        setExpiryDatesObj(expiryDates);

        if (sweetener_offer?.offer_expiry?.offer_expiry_type) {
          if (sweetener_offer?.offer_expiry?.offer_expiry_type === EXPIRY_DATE) {
            setPromoExpiry({
              EXPIRY_DATE: sweetener_offer?.offer_expiry?.expiry_dates,
            });
            setShowDatePickerForOfferSweetnerID(1);
          } else if (sweetener_offer?.offer_expiry?.offer_expiry_type === LAST_INSTALLATION_DATE) {
            setPromoExpiry({
              LAST_INSTALLATION_DATE: sweetener_offer?.offer_expiry?.expiry_dates,
            });
            setShowDatePickerForOfferSweetnerID(2);
          } else if (
            sweetener_offer?.offer_expiry?.offer_expiry_type ===
            LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE
          ) {
            setPromoExpiry({
              LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE:
                sweetener_offer?.offer_expiry?.expiry_dates,
            });
            setShowDatePickerForOfferSweetnerID(3);
          }
        }

        const hasTemplateVariables =
          offersList.find((offer) => offer.id === sweetener_offer.headline_builder_id)
            ?.template_variables?.length > 0;

        setDisableConfig(!hasTemplateVariables && !sweetener_offer.custom);
      }
      valuesLoadedInitially.current = true;
    }

    if (!hasDiscountOffer && !hasFinanceOffer) {
      setIsSweetnerClickable(false);
      setIncludeSweetnerOffer(false);
      setSelectHeadline('none');
      setDisableConfig(true);
      setCustomHeadline('');
      setCustomDisclaimer('');
      setPromoExpiry({});
      setExpiryDatesObj([]);
      if (promotionData?.sweetener_offer !== undefined) {
        delete promotionData.sweetener_offer;
        setPromotionData({
          ...promotionData,
        });
      }
    }
  }, [promotionData, loadedPromotionData, offersList, selectHeadline]);

  const handleOfferInclude = (event) => {
    setIncludeSweetnerOffer(event.target.checked);
    if (!event.target.checked) {
      setIncludeAppointmentDisclaimer(false);
      setPromoExpiry({});
      setExpiryDatesObj([]);
      setSelectHeadline('none');
      setDisableConfig(true);
      setCustomHeadline('');
      setCustomDisclaimer('');
      delete promotionData?.sweetener_offer;
      setPromotionData({
        ...promotionData,
      });
    } else {
      setPromotionData({
        ...promotionData,
        sweetener_offer: {},
      });
    }
  };

  const handleHeadlineChange = (value) => {
    setSelectHeadline(value);
    const isCustomOffer = value === 'custom_offer';

    // Same data update regardless of promoExpiry, simplified code
    if (promoExpiry !== undefined && Object.keys(promoExpiry).length === 0) {
      setShowDatePickerForOfferSweetnerID(1);
    }

    setPromotionData({
      ...promotionData,
      sweetener_offer: {
        ...promotionData.sweetener_offer,
        headline: isCustomOffer ? '' : value,
        ...(isCustomOffer ? {} : { headline_builder_id: value }),
        custom: isCustomOffer,
      },
    });

    const hasTemplateVariables =
      offersList.find((offer) => offer.id === value)?.template_variables?.length > 0;

    setDisableConfig(!hasTemplateVariables && !isCustomOffer);
    setVariableValues({});
    setHeadlineOfferValues({});
    setConfigureHeadline(false);
    setCustomHeadline('');
    setCustomDisclaimer('');
  };

  const handleCustomDisclaimer = (value) => {
    setCustomDisclaimer(value);
    setPromotionData({
      ...promotionData,
      sweetener_offer: {
        ...promotionData.sweetener_offer,
        disclaimer: value,
      },
    });
  };

  const handleCustomHeadline = (value) => {
    setCustomHeadline(value);
    setPromotionData({
      ...promotionData,
      sweetener_offer: {
        ...promotionData.sweetener_offer,
        headline: value,
      },
    });
  };

  const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const renderModifiedHeadline = (offersList, headline) => {
    const requiredHeadline = offersList.find((eachOffer) => eachOffer.id === headline);
    const template = requiredHeadline?.template;
    const templateVariables = requiredHeadline?.template_variables;

    if (templateVariables) {
      const pattern = new RegExp(`(${templateVariables?.map(escapeRegExp).join('|')}|\\s)`);
      const parts = template.split(pattern);

      return parts.map((part, partIndex) => {
        if (templateVariables.includes(part)) {
          const variableIndex = templateVariables.indexOf(part);

          // Get options based on variable index
          let options = [];

          if (variableIndex === 0) {
            options = valuesForD1.map((value) => value.toString());
          } else if (variableIndex === 1) {
            options = valuesForD2.map((value) => value.toString());
          } else {
            options = valuesForD3.map((value) => value.toString());
          }

          return {
            type: 'autocomplete',
            key: `template-var-${partIndex}_${part}`,
            part,
            options,
            value: Object.keys(promotionData)?.length
              ? promotionData?.sweetener_offer?.custom_values?.X
              : headlineOfferValues[part]?.toString(),
          };
        }
        return {
          type: 'text',
          key: `text-part-${partIndex}_${part}`,
          text: part,
        };
      });
    }
    return [];
  };

  const filterDropdownValues = (selectedValue, part) => {
    const requiredHeadline = offersList.find((eachOffer) => eachOffer.id === selectHeadline);

    if (!requiredHeadline) {
      return;
    }

    const templateVariables = requiredHeadline.template_variables;
    const index = templateVariables.indexOf(part);

    if (requiredHeadline?.nested_values && Object.keys(requiredHeadline.nested_values).length > 0) {
      if (index === 0 && requiredHeadline?.nested_values[selectedValue]) {
        setValuesForD2(Object.keys(requiredHeadline?.nested_values[selectedValue]));
      } else if (index === 1) {
        const previousVarKey = templateVariables[index - 1];
        const previousVarValue = variableValues[previousVarKey];

        if (requiredHeadline?.nested_values?.[previousVarValue]?.[selectedValue]) {
          setValuesForD3(requiredHeadline?.nested_values[previousVarValue][selectedValue]);
        }
      }
    }
  };

  const handleAutocompleteChange = (value, part) => {
    const tempObj = { ...variableValues };

    tempObj[part] = value;
    setVariableValues(tempObj);
    setHeadlineOfferValues({ ...headlineOfferValues, ...tempObj });

    const newPromotionData = {
      ...promotionData,
      sweetener_offer: {
        ...promotionData.sweetener_offer,
        custom_values: {
          ...promotionData.sweetener_offer.custom_values,
        },
      },
    };

    const isValidValue = value !== '' && value !== null && value !== undefined && value >= 0;

    if (isValidValue) {
      newPromotionData.sweetener_offer.custom_values[part] = value;
    } else {
      delete newPromotionData.sweetener_offer.custom_values[part];
    }

    if (Object.keys(newPromotionData.sweetener_offer.custom_values).length === 0) {
      delete newPromotionData.sweetener_offer.custom_values;
    }

    setPromotionData(newPromotionData);
    filterDropdownValues(value, part);
  };

  const handleOfferExpiryDate = (data) => {
    if (data !== null) {
      const date = formatDateToMDY(data);

      if (promoExpiry[EXPIRY_DATE] === undefined || date !== promoExpiry[EXPIRY_DATE][0]) {
        setExpiryDatesObj([
          { type: 'expiresOn', id: showDatePickerForOfferSweetnerID, value: date },
        ]);

        setPromotionData({
          ...promotionData,
          sweetener_offer: {
            ...promotionData.sweetener_offer,
            offer_expiry: { expiry_dates: [date], offer_expiry_type: EXPIRY_DATE },
          },
        });
        setPromoExpiry({ EXPIRY_DATE: date });
      }
    }
  };

  const handleOfferExpiryDateForInstallation = (data) => {
    const date = formatDateToMDY(data);

    if (
      promoExpiry[LAST_INSTALLATION_DATE] === undefined ||
      date !== promoExpiry[LAST_INSTALLATION_DATE][0]
    ) {
      setExpiryDatesObj([
        { type: 'scheduleBefore', id: showDatePickerForOfferSweetnerID, value: data },
      ]);
      setPromotionData({
        ...promotionData,
        sweetener_offer: {
          ...promotionData.sweetener_offer,
          offer_expiry: {
            expiry_dates: [date],
            offer_expiry_type: LAST_INSTALLATION_DATE,
          },
        },
      });
      setPromoExpiry({ LAST_INSTALLATION_DATE: date });
    }
  };

  const handleScheduleExpDate = (data) => {
    const date = formatDateToMDY(data);
    let updatedDatesObj = expiryDatesObj || [];

    const hasOtherExpiryType =
      updatedDatesObj[0] !== undefined &&
      updatedDatesObj[0].offer_expiry_type !== LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE;

    if (hasOtherExpiryType) {
      updatedDatesObj = [
        {
          id: 31,
          value: date,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        },
      ];
    } else {
      let found = false;

      updatedDatesObj?.map((eachDate, index) => {
        if (eachDate.id === 31) {
          updatedDatesObj[index].value = date;
          found = true;
        }
        return null;
      });

      if (!found) {
        updatedDatesObj = [
          ...updatedDatesObj,
          {
            id: 31,
            value: date,
            offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
          },
        ];
      }
    }

    setExpiryDatesObj(updatedDatesObj);
    const dates = updatedDatesObj
      .sort((a, b) => a.id - b.id)
      .map((obj) => obj.value)
      ?.filter((value) => value !== null && value !== undefined);

    setPromotionData({
      ...promotionData,
      sweetener_offer: {
        ...promotionData.sweetener_offer,
        offer_expiry: {
          expiry_dates: dates,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        },
      },
    });

    setPromoExpiry({ LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE: dates });
  };

  const handlePurchaseExpDate = (data) => {
    const date = formatDateToMDY(data);
    let updatedDatesObj = expiryDatesObj || [];

    const hasOtherExpiryType =
      updatedDatesObj[0] !== undefined &&
      updatedDatesObj[0].offer_expiry_type !== LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE;

    if (hasOtherExpiryType) {
      updatedDatesObj = [
        {
          id: 32,
          value: date,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        },
      ];
    } else {
      let found = false;

      updatedDatesObj?.map((eachDate, index) => {
        if (eachDate.id === 32) {
          updatedDatesObj[index].value = date;
          found = true;
        }
        return null;
      });

      if (!found) {
        updatedDatesObj = [
          ...updatedDatesObj,
          {
            id: 32,
            value: date,
            offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
          },
        ];
      }
    }

    setExpiryDatesObj(updatedDatesObj);
    const dates = updatedDatesObj
      .sort((a, b) => a.id - b.id)
      .map((obj) => obj.value)
      ?.filter((value) => value !== null && value !== undefined);

    setPromotionData({
      ...promotionData,
      sweetener_offer: {
        ...promotionData.sweetener_offer,
        offer_expiry: {
          expiry_dates: dates,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        },
      },
    });

    setPromoExpiry({ LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE: dates });
  };

  const handleAppointmentDisInclude = (event) => {
    setIncludeAppointmentDisclaimer(event.target.checked);
    if (!event.target.checked) {
      delete promotionData?.sweetener_offer?.initial_appointment_disclaimer;
      setPromotionData({
        ...promotionData,
      });
    } else {
      setPromotionData({
        ...promotionData,
        sweetener_offer: {
          ...promotionData.sweetener_offer,
          initial_appointment_disclaimer: SWEETNER_DISCLAIMER,
        },
      });
    }
  };

  const toggleConfigureHeadline = () => {
    setConfigureHeadline(!configureHeadline);
  };

  const isCardDisabled = !includeSweetnerOffer;
  const isArchived = mode === 'Edit' && promotionData?.status === 'ARCHIVED';

  return {
    // State values
    showDatePickerForOfferSweetnerID,
    setShowDatePickerForOfferSweetnerID,
    includeSweetnerOffer,
    configureHeadline,
    selectHeadline,
    disableConfig,
    customHeadline,
    customDisclaimer,
    promoExpiry,
    isSweetnerClickable,
    includeAppointmentDisclaimer,
    modifiedOfferData,
    isCardDisabled,
    isArchived,

    // Event handlers
    handleOfferInclude,
    handleHeadlineChange,
    handleCustomHeadline,
    handleCustomDisclaimer,
    handleAutocompleteChange,
    handleOfferExpiryDate,
    handleOfferExpiryDateForInstallation,
    handleScheduleExpDate,
    handlePurchaseExpDate,
    handleAppointmentDisInclude,
    toggleConfigureHeadline,
    renderModifiedHeadline,
  };
};
